import React, {
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { Modal, TextField, Select } from '@gerdau/hefesto';
import { AddUserModalContent, Container, Form, SelectContent } from './styles';
import { AccessInternalManagementService } from '../../../../services/AccessInternalManagement';
import { addToast } from '../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../store/ducks/toast/types';
import { useDispatch } from 'react-redux';
import { IInternalUser } from '../../../../store/ducks/internal-user/types';

interface IAddInternalUserModalProps {
  onClose(): void;
  setSellerReps: React.Dispatch<React.SetStateAction<IInternalUser[]>>;
}

interface ISelectOptions {
  value: string;
  label: string;
}

interface IFormState {
  firstName: 'valid' | 'invalid';
  lastName: 'valid' | 'invalid';
  email: 'valid' | 'invalid';
  repCode: 'valid' | 'invalid';
}

interface IValidationMessages {
  firstName: string;
  lastName: string;
  email: string;
  repCode: string;
}
const AddInternalUserModal: React.FC<IAddInternalUserModalProps> = ({
  onClose,
  setSellerReps
}) => {
  const [selectOptions, setSelectlectOptions] = useState<ISelectOptions[]>([]);
  const [selectedRepCode, setSelectedRepCode] = useState<string | string[]>('');
  const [isLoading, setIsLoading] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dispatch = useDispatch();

  const [formState, setFormState] = useState<IFormState>({
    firstName: 'valid',
    lastName: 'valid',
    email: 'valid',
    repCode: 'valid'
  });

  const handleToastValidation = useCallback((message) => {
    dispatch(
      addToast({
        message: message,
        type: EToastTypes.DANGER,
        durationInMS: 5000
      })
    );
  }, []);

  const handleValidationForm = useCallback(
    async (firstName: string, lastName: string, email: string) => {
      const firstNameRegex = /^[a-zA-ZÀ-ÿ ]{1,}$/;
      const lastNameRegex = /^[a-zA-ZÀ-ÿ ]{1,}$/;
      const emailRegex = /^[^\s@]+@(?:\w+\.)?gerdau(\.com|\.com\.br)$/;

      const firstNameValid = firstNameRegex.test(firstName);
      const lastNameValid = lastNameRegex.test(lastName);
      const emailValid = emailRegex.test(email);

      const newFormState: IFormState = {
        firstName: firstNameValid ? 'valid' : 'invalid',
        lastName: lastNameValid ? 'valid' : 'invalid',
        email: emailValid ? 'valid' : 'invalid',
        repCode: selectedRepCode.length > 0 ? 'valid' : 'invalid'
      };

      const invalidMessages: IValidationMessages = {
        firstName: 'Nome Inválido, insira o nome novamente!',
        lastName: 'Sobrenome Inválido, insira o sobrenome novamente!',
        email: 'E-mail inválido, insira um e-mail gerdau válido!',
        repCode: 'Escolha um código de representante válido!'
      };

      Object.entries(newFormState).forEach(([key, value]) => {
        if (value === 'invalid') {
          const toastMessage =
            invalidMessages[key as keyof IValidationMessages];
          if (toastMessage) {
            handleToastValidation(toastMessage);
          }
        }
      });

      const anyInvalid = Object.values(newFormState).find(
        (el: string) => el === 'invalid'
      );

      setFormState(newFormState);

      return anyInvalid;
    },
    [selectedRepCode]
  );

  const handleSaveUser = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setIsLoading(true);
      const form = new FormData(event.target as HTMLFormElement);

      const dataForm = Object.fromEntries(form);

      const firstName = dataForm.firstName.toString().trim();
      const lastName = dataForm.lastName.toString().trim();
      const email = dataForm.email.toString().trim();

      const validation = await handleValidationForm(firstName, lastName, email);

      if (validation) {
        setIsLoading(false);
        return;
      }

      const bodyData = {
        name: firstName.concat(' ', lastName),
        email: email,
        salesRepCode: [selectedRepCode] as string[],
        coreTeam: false,
        searchCustomerType: 'soldTo'
      };

      AccessInternalManagementService.createSellerRep(bodyData)
        .then((res) => {
          dispatch(
            addToast({
              message: `Usuário ${bodyData.name} cadastrado com sucesso`,
              type: EToastTypes.SUCCESS
            })
          );

          setSellerReps((oldState) => [...oldState, res]);
          setIsLoading(false);
          onClose();
        })
        .catch(() => {
          dispatch(
            addToast({
              message: `Erro ao tentar cadastrar o Usuário.`,
              type: EToastTypes.DANGER
            })
          );
          setIsLoading(false);
        });
    },
    [selectedRepCode, setIsLoading, formState, handleValidationForm]
  );

  useEffect(() => {
    AccessInternalManagementService.getSellerRepCodes()
      .then((res) =>
        res.map((option) => {
          return {
            value: option.code,
            label: `${option.code} - ${option.description}`
          };
        })
      )
      .then((result) => setSelectlectOptions(result))
      .catch((error) => {
        console.error(error);
        dispatch(
          addToast({
            message: 'Erro ao carregar os códigos de vendedores.',
            type: EToastTypes.DANGER
          })
        );
      });
  }, []);

  return (
    <Container>
      <Modal
        onClose={() => (isLoading ? undefined : onClose())}
        modalSize="sm"
        title={'Adicionar usuário interno'}
        buttons={{
          left: {
            children: 'Cancelar',
            name: 'Cancelar',
            onClick: () => {
              onClose();
            },
            type: 'button',
            disabled: isLoading
          },
          right: {
            children: 'Adicionar usuário',
            name: 'Adicionar usuário',
            onClick: () => {
              const saveButton = buttonRef.current;
              saveButton?.click();
            },
            type: 'button',
            loading: isLoading,
            disabled: isLoading
          }
        }}
      >
        <span className="subtitle" data-testid="subtitle">
          O novo usuário receberá os dados de acesso através do email cadastrado
          neste formulário.
        </span>
        <Form id="create-user-form" onSubmit={handleSaveUser}>
          <AddUserModalContent>
            <TextField
              label={'Nome'}
              name={'firstName'}
              placeholder="Digite o nome"
              variant={formState.firstName === 'valid' ? 'neutral' : 'danger'}
              onFocus={() =>
                setFormState((oldState) => ({
                  ...oldState,
                  firstName: 'valid'
                }))
              }
              disabled={isLoading}
            />
            <TextField
              label={'Sobrenome'}
              name={'lastName'}
              placeholder="Digite o sobrenome"
              variant={formState.lastName === 'valid' ? 'neutral' : 'danger'}
              onFocus={() =>
                setFormState((oldState) => ({
                  ...oldState,
                  lastName: 'valid'
                }))
              }
              disabled={isLoading}
            />
          </AddUserModalContent>
          <SelectContent>
            <Select
              label={'Código do vendedor'}
              name={'sellerRepCode'}
              placeholder="Digite o código"
              options={selectOptions}
              onSelect={(value: string | string[]) => {
                setSelectedRepCode(value);
              }}
              disabled={selectOptions.length < 1 || isLoading}
              variant={formState.repCode === 'valid' ? 'neutral' : 'danger'}
              onFocus={() =>
                setFormState((oldState) => ({
                  ...oldState,
                  repCode: 'valid'
                }))
              }
            />
          </SelectContent>
          <AddUserModalContent>
            <TextField
              label={'Email'}
              name={'email'}
              placeholder="exemplo@email.com"
              variant={formState.email === 'valid' ? 'neutral' : 'danger'}
              onFocus={() =>
                setFormState((oldState) => ({
                  ...oldState,
                  email: 'valid'
                }))
              }
              disabled={isLoading}
            />
          </AddUserModalContent>
          <button
            ref={buttonRef}
            style={{ display: 'none' }}
            type="submit"
            disabled={isLoading}
          ></button>
        </Form>
      </Modal>
    </Container>
  );
};

export default AddInternalUserModal;
